import { createContext, useContext, useEffect, useRef, useState } from 'react';

/* Scroll-reveal hooks for the sections below the hero.

   Every animated section root carries data-reveal:
   - "wait": not seen yet. Animated parts are hidden (opacity 0).
   - "play": scrolled into view once. CSS entrance animations run (both fill).
   - "done": no animation, everything in its final frame. Used for
     ?settled=1 (full-page audit shots) and prefers-reduced-motion.
   `live` is true only while the section is on screen, so loops (wallpaper
   drift, Buffy's gestures, the live dot) pause off screen.
   `near` turns true ~900px before the section arrives, so wallpapers load
   just in time (always true when settled). */

function readSettled() {
  if (typeof window === 'undefined') return true;
  try {
    if (new URLSearchParams(window.location.search).get('settled') === '1') return true;
  } catch (e) {
    /* ignore a malformed query string */
  }
  return false;
}

/* Owner 2026-09-29: every animation a little slower so it is easy to
   follow. JS timers multiply by SLOW; CSS reads the same factor from
   --cxs-slow (sections.css). Delays passed to CSS as --d stay in unscaled
   ms, because the CSS scales them. */
export const SLOW = 1.35;

/* Page switches settle every below-hero scene, including unseen scenes. */
export const StillContext = createContext(false);
export const TeacherMotionContext = createContext(null);

export function useStill() {
  const [settled] = useState(readSettled);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const forced = useContext(StillContext);
  return settled || reduced || forced;
}

export function useMedia(query) {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [match, setMatch] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, [query]);
  return match;
}

export function useReveal({ amount = 0.3, delay = 0 } = {}) {
  const ref = useRef(null);
  // Keep observing while paused, so resuming still respects visibility.
  const [urlStill] = useState(readSettled);
  const still = useStill();
  const [reveal, setReveal] = useState(urlStill ? 'done' : 'wait');
  const [live, setLive] = useState(false);
  const [near, setNear] = useState(urlStill);

  useEffect(() => {
    const el = ref.current;
    if (urlStill || !el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setReveal('done');
      setNear(true);
      return undefined;
    }
    let timer = null;
    const start = () => {
      if (timer || still) return;
      timer = setTimeout(() => setReveal((r) => (r === 'wait' ? 'play' : r)), delay);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        setLive(e.isIntersecting);
        // A section taller than the screen can never reach `amount` of its
        // own height, so also accept "fills most of the screen".
        const vh = window.innerHeight || 1;
        const enough = e.intersectionRatio >= amount || e.intersectionRect.height >= vh * 0.6;
        if (e.isIntersecting && enough) start();
      });
    }, { threshold: [0, 0.1, 0.2, amount, 0.5, 0.75, 1] });
    const nearIo = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setNear(true);
        nearIo.disconnect();
      }
    }, { rootMargin: '900px 0px' });
    io.observe(el);
    nearIo.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      io.disconnect();
      nearIo.disconnect();
    };
  }, [urlStill, amount, delay, still]);

  return { ref, reveal: still ? 'done' : reveal, live: still ? false : live, near: still || near, still };
}
